import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Upload,
  BarChart3,
  Users,
  Zap,
  Brain,
  Shield,
  TrendingUp,
  Sparkles,
  FileImage,
  Palette,
  GitBranch,
  FolderOpen,
  CheckCircle,
  Target,
  Eye,
  RotateCcw
} from 'lucide-react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AutoplayVideo } from './AutoplayVideo';

interface OnboardingStep {
  id: string;
  stepNumber: string;
  title: string;
  description: string;
  videoSrc: string;
  features: Array<{
    icon: React.ReactNode;
    text: string;
  }>;
  isVideoLeft: boolean;
}

export function OnboardingModal() {
  const { isActive, currentStep, totalSteps, nextStep, previousStep, skipOnboarding, completeStep } = useOnboarding();
  const { user } = useAuth();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Define video-centric tutorial steps
  const steps: OnboardingStep[] = [
    {
      id: 'ai-generation',
      stepNumber: '01',
      title: 'Create Professional Ads in Seconds',
      description: 'Generate stunning, on-brand creatives from just a brief and logo',
      videoSrc: '/Ad_generation_tutorial.webm',
      features: [
        {
          icon: <Sparkles className="w-5 h-5 text-purple-600" />,
          text: 'Generate from brief and logo'
        },
        {
          icon: <Palette className="w-5 h-5 text-blue-600" />,
          text: 'Multiple style options available'
        },
        {
          icon: <RotateCcw className="w-5 h-5 text-green-600" />,
          text: 'Regenerate with feedback until perfect'
        }
      ],
      isVideoLeft: true
    },
    {
      id: 'upload-analysis',
      stepNumber: '02',
      title: 'Instant Compliance Check',
      description: 'Upload your creative and get comprehensive analysis in seconds',
      videoSrc: '/Analysis_video_tutorial.webm',
      features: [
        {
          icon: <FileImage className="w-5 h-5 text-blue-600" />,
          text: 'Support for images up to 20MB, videos up to 1GB'
        },
        {
          icon: <Shield className="w-5 h-5 text-green-600" />,
          text: 'Instant multi-platform compliance checking'
        },
        {
          icon: <Brain className="w-5 h-5 text-purple-600" />,
          text: 'AI-powered performance predictions'
        }
      ],
      isVideoLeft: false
    },
    {
      id: 'ai-enhancement',
      stepNumber: '03',
      title: 'Transform Your Creatives',
      description: 'Let AI enhance and optimize your creatives for maximum impact',
      videoSrc: '/Enhance_video_tutorial.webm',
      features: [
        {
          icon: <Target className="w-5 h-5 text-red-600" />,
          text: 'Auto-fix compliance issues'
        },
        {
          icon: <Eye className="w-5 h-5 text-orange-600" />,
          text: 'Color & composition optimization'
        },
        {
          icon: <BarChart3 className="w-5 h-5 text-blue-600" />,
          text: 'Platform-specific cropping and sizing'
        }
      ],
      isVideoLeft: true
    },
    {
      id: 'collaboration',
      stepNumber: '04',
      title: 'Track & Collaborate',
      description: 'Manage versions and work seamlessly with your team',
      videoSrc: '/Versioning_video_tutorial.webm',
      features: [
        {
          icon: <GitBranch className="w-5 h-5 text-purple-600" />,
          text: 'Complete version history tracking'
        },
        {
          icon: <Users className="w-5 h-5 text-blue-600" />,
          text: 'Real-time team collaboration'
        },
        {
          icon: <FolderOpen className="w-5 h-5 text-green-600" />,
          text: 'Organized project management'
        }
      ],
      isVideoLeft: false
    }
  ];

  const currentStepData = steps[currentStep];

  // Auto-scroll to top when step changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!isActive) {
    return null;
  }

  const handleNext = () => {
    completeStep(currentStepData.id);
    nextStep();
    // Scroll to top of modal after step change
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handlePrevious = () => {
    previousStep();
    // Scroll to top of modal after step change
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] sm:max-h-[95vh] overflow-hidden flex flex-col mx-2 sm:mx-4"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 sm:px-8 py-3 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-purple-200">
                    {currentStepData.stepNumber}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base sm:text-lg lg:text-2xl font-bold text-white">{currentStepData.title}</h2>
                    <p className="text-purple-100 mt-1 text-xs sm:text-sm lg:text-base">{currentStepData.description}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={skipOnboarding}
                className="text-white/80 hover:text-white transition-colors p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-100 px-4 sm:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">
                Step {currentStep + 1} of {totalSteps}
              </span>
              <span className="text-xs text-gray-500">
                {Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full"
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Main Content - Video + Features Side by Side */}
          <div ref={scrollContainerRef} className="flex-1 px-4 sm:px-8 py-4 sm:py-6 lg:py-8 overflow-y-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-center h-full ${!currentStepData.isVideoLeft ? 'lg:grid-flow-col-dense' : ''}`}>
              {/* Video Section */}
              <div className={`${currentStepData.isVideoLeft ? 'lg:order-1' : 'lg:order-2'}`}>
                <AutoplayVideo
                  key={`video-${currentStep}-${currentStepData.id}`}
                  src={currentStepData.videoSrc}
                  className="aspect-video border border-purple-400/20 hover:border-purple-400/40 transition-all rounded-xl"
                  threshold={0.3}
                />
              </div>

              {/* Content Section */}
              <div className={`space-y-6 ${currentStepData.isVideoLeft ? 'lg:order-2' : 'lg:order-1'}`}>
                <div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    How it works
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed">
                    {currentStepData.description}
                  </p>
                </div>

                {/* Features List */}
                <div className="space-y-2 sm:space-y-4">
                  {currentStepData.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm border border-gray-100">
                        {feature.icon}
                      </div>
                      <span className="text-gray-700 font-medium text-sm sm:text-base">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Call to Action */}
                <div className="pt-4">
                  <button
                    onClick={() => {
                      const routes = {
                        'ai-generation': '/dashboard/ad-generator', // AI Ad Generator page
                        'upload-analysis': '/dashboard/analysis', // Upload & Analysis page
                        'ai-enhancement': '/dashboard/analysis', // Analysis page for enhancement
                        'collaboration': '/dashboard/projects' // Projects for collaboration
                      };
                      skipOnboarding(); // Close modal first
                      navigate(routes[currentStepData.id as keyof typeof routes] || '/dashboard');
                    }}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 min-h-[44px] text-sm sm:text-base"
                  >
                    <span>
                      {currentStepData.id === 'ai-generation' && 'Try AI Generation'}
                      {currentStepData.id === 'upload-analysis' && 'Upload Creative'}
                      {currentStepData.id === 'ai-enhancement' && 'Try Enhancement'}
                      {currentStepData.id === 'collaboration' && 'View Projects'}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 sm:px-8 py-3 sm:py-4 lg:py-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div className="flex items-center space-x-3 order-2 sm:order-1">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrevious}
                    className="flex items-center space-x-2 px-3 sm:px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors text-sm sm:text-base min-h-[44px]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                )}
                <button
                  onClick={skipOnboarding}
                  className="text-gray-500 hover:text-gray-700 text-xs sm:text-sm min-h-[44px] py-2 px-3"
                >
                  Skip tour
                </button>
              </div>
              <button
                onClick={handleNext}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 order-1 sm:order-2 text-sm sm:text-base min-h-[44px]"
              >
                <span>{currentStep === totalSteps - 1 ? 'Complete Tour' : 'Next'}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}